import { Box, Button, Typography, makeStyles } from "@material-ui/core";
import { AccessTime, Description, Person } from "@mui/icons-material";
import themeColorsDesingSystem from "assets/theme/ds-colors";

const useStyles = makeStyles({
  container: {
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    borderRadius: 8,
    padding: "1.5rem 2rem",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "2rem",
  },
  title: {
    fontSize: "1rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.text.primary,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  count: {
    fontSize: "0.875rem",
    color: themeColorsDesingSystem.gray.main,
    fontWeight: 500,
  },
  timelineItem: {
    position: "relative",
    display: "flex",
    gap: 20,
    marginBottom: "2rem",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  timelineLine: {
    position: "absolute",
    top: 44,
    left: 19,
    bottom: -32,
    width: 2,
    backgroundColor: themeColorsDesingSystem.stone.main,
  },
  iconCircle: {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#a5b4fc",
    color: "#312e81",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  logCard: {
    flex: 1,
    backgroundColor: themeColorsDesingSystem.background.main,
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.15s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  logHeader: {
    padding: "0.875rem 1.25rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  logAction: {
    fontWeight: 700,
    fontSize: "0.9375rem",
    color: themeColorsDesingSystem.text.primary,
  },
  logTime: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.main,
    fontWeight: 500,
  },
  logBody: {
    padding: "1rem 1.25rem",
    backgroundColor: "#f8fafc",
    color: themeColorsDesingSystem.gray.dark,
    fontSize: "0.875rem",
    lineHeight: 1.6,
    borderTop: `1px solid ${themeColorsDesingSystem.stone.light}`,
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
  },
  logFooter: {
    padding: "0.75rem 1.25rem",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.main,
    fontWeight: 500,
  },
  loadMore: {
    textAlign: "center",
    paddingTop: "1rem",
    paddingBottom: "0.5rem",
  },
  loadMoreButton: {
    fontSize: "0.875rem",
    color: themeColorsDesingSystem.primary.main,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  emptyState: {
    fontSize: "0.875rem",
    color: themeColorsDesingSystem.gray.main,
    textAlign: "center",
    padding: "2rem 0",
  },
});

function mapTimelineLog(log) {
  return {
    id: log.Id,
    acao: log.Acao,
    data: log.Data,
    hora: log.Hora,
    usuario: log.Usuario,
    sistema: log.Sistema,
    detalhesExtras: log.DetalhesExtras ?? [],
  };
}

export default function LogsTimeline({
  logs = [],
  totalRegistros = 0,
  loading = false,
  hasMore = false,
  onLoadMore,
}) {
  const classes = useStyles();
  const mappedLogs = logs.map(mapTimelineLog);

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          <AccessTime />
          Histórico de Rastreamento
        </Typography>
        <Typography className={classes.count}>
          {totalRegistros || mappedLogs.length} registro(s) encontrado(s)
        </Typography>
      </Box>

      {!mappedLogs.length && !loading && (
        <Typography className={classes.emptyState}>
          Nenhum registro encontrado para o período selecionado.
        </Typography>
      )}

      <Box position="relative">
        {mappedLogs.map((log, index) => (
          <Box key={log.id} className={classes.timelineItem}>
            {index !== mappedLogs.length - 1 && (
              <Box className={classes.timelineLine} />
            )}

            <Box className={classes.iconCircle}>
              <Description style={{ fontSize: 18 }} />
            </Box>

            <Box className={classes.logCard}>
              <Box className={classes.logHeader}>
                <Typography className={classes.logAction}>
                  {log.acao}
                </Typography>
                <Box className={classes.logTime}>
                  <AccessTime style={{ fontSize: 14 }} />
                  <span>
                    {log.data} às {log.hora}
                  </span>
                </Box>
              </Box>

              <Box className={classes.logBody}>
                {log.detalhesExtras?.map((detalhe, i) => (
                  <Typography
                    key={i}
                    style={{
                      marginBottom: i < log.detalhesExtras.length - 1 ? 4 : 0,
                    }}
                  >
                    {detalhe}
                  </Typography>
                ))}
              </Box>

              <Box className={classes.logFooter}>
                <Person style={{ fontSize: 14 }} />
                <span>Por {log.usuario}</span>
              </Box>
            </Box>
          </Box>
        ))}

        {hasMore && (
          <Box className={classes.loadMore}>
            <Button
              className={classes.loadMoreButton}
              disabled={loading}
              onClick={onLoadMore}
            >
              {loading ? "Carregando..." : "Carregar mais registros..."}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
